import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { KnowledgeGraphSummary, RelationshipType, SchemaData } from '../../types/api-response';
import '../shared.css';
import './SummaryViewer.css';

interface SummaryViewerProps {
  kgId: string;
  versionId: string;
}

type SortDir = 'asc' | 'desc';

function Bar({ value, max, pctLabel }: { value: number; max: number; pctLabel?: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="sv2-bar-cell">
      <div className="sv2-bar-wrap">
        <div className="sv2-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      {pctLabel && <span className="sv2-bar-pct">{pctLabel}</span>}
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString();
}

// Property sub-row rendered inside a collapsed panel
function PropRows({ props }: { props: Record<string, { type: string }> }) {
  const entries = Object.entries(props);
  if (!entries.length) return <div className="sv2-prop-empty">No properties</div>;
  return (
    <div className="sv2-prop-grid">
      {entries.map(([k, v]) => (
        <div key={k} className="sv2-prop-row">
          <span className="sv2-prop-name">{k}</span>
          <span className="sv2-prop-type">{v.type}</span>
        </div>
      ))}
    </div>
  );
}


const SummaryViewer: React.FC<SummaryViewerProps> = ({ kgId, versionId }) => {
  const [summary, setSummary] = useState<KnowledgeGraphSummary | null>(null);
  const [schema,  setSchema]  = useState<SchemaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const [nodeSort, setNodeSort] = useState<SortDir>('desc');
  const [relSort,  setRelSort]  = useState<SortDir>('desc');
  const [nodeFilter, setNodeFilter] = useState('');
  const [relFilter,  setRelFilter]  = useState('');

  // Track which rows are expanded: key = label or relType+source+target+index
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [expandedRels,  setExpandedRels]  = useState<Set<string>>(new Set());

  const toggleNode = (key: string) =>
    setExpandedNodes(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  const toggleRel = (key: string) =>
    setExpandedRels(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([
      apiService.getERDSummary({ kg_id: kgId, version_id: versionId }),
      apiService.getERDSchema({ kg_id: kgId, version_id: versionId }).catch(() => null),
    ]).then(([sumRes, schRes]: [any, any]) => {
      const s = sumRes?.data?.attributes?.summary;
      if (s) setSummary(s);
      else setError('No summary data returned');
      const sc = schRes?.data?.attributes?.schema;
      if (sc) setSchema(sc);
    }).catch((e: any) => setError(e?.message ?? 'Failed to load summary'))
      .finally(() => setLoading(false));
  }, [kgId, versionId]);


  if (loading) {
    return (
      <div className="sv2-root tab-root">
        <div className="tab-loader"><span className="tab-spinner" /></div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="sv2-root tab-root">
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error || 'No data'}</span>
        </div>
      </div>
    );
  }

  // ── Node rows ───────────────────────────────────────────────────────────
  const maxNodeCount = Math.max(...Object.values(summary.labels), 0);
  const nodeRows = Object.entries(summary.labels)
    .filter(([label]) => label.toLowerCase().includes(nodeFilter.toLowerCase()))
    .sort(([, a], [, b]) => nodeSort === 'desc' ? b - a : a - b);

  // ── Rel rows ────────────────────────────────────────────────────────────
  const relFlat: { type: string; source: string; target: string; count: number }[] = [];
  Object.entries(summary.relTypes).forEach(([type, connections]: [string, RelationshipType[]]) => {
    connections.forEach(c => relFlat.push({ type, source: c.source, target: c.target, count: c.count }));
  });
  const maxRelCount = Math.max(...relFlat.map(r => r.count), 0);
  const totalRelFlat = relFlat.reduce((s, r) => s + r.count, 0);
  const relRows = relFlat
    .filter(r =>
      r.type.toLowerCase().includes(relFilter.toLowerCase()) ||
      r.source.toLowerCase().includes(relFilter.toLowerCase()) ||
      r.target.toLowerCase().includes(relFilter.toLowerCase())
    )
    .sort((a, b) => relSort === 'desc' ? b.count - a.count : a.count - b.count);

  const toggleNodeSort = () => setNodeSort(s => s === 'desc' ? 'asc' : 'desc');
  const toggleRelSort  = () => setRelSort(s  => s === 'desc' ? 'asc' : 'desc');

  return (
    <div className="sv2-root tab-root">
      {/* ── Stat pills ─────────────────────────────────────────────────── */}
      <div className="sv2-stats-bar">
        <div className="sv2-stat">
          <span className="sv2-stat-value">{fmt(summary.nodeCount)}</span>
          <span className="sv2-stat-label">Total Nodes</span>
        </div>
        <div className="sv2-stat-sep" />
        <div className="sv2-stat">
          <span className="sv2-stat-value">{fmt(summary.relCount)}</span>
          <span className="sv2-stat-label">Total Relationships</span>
        </div>
        <div className="sv2-stat-sep" />
        <div className="sv2-stat">
          <span className="sv2-stat-value">{summary.labelCount}</span>
          <span className="sv2-stat-label">Node Labels</span>
        </div>
        <div className="sv2-stat-sep" />
        <div className="sv2-stat">
          <span className="sv2-stat-value">{summary.relTypeCount}</span>
          <span className="sv2-stat-label">Rel Types</span>
        </div>
        <div className="sv2-stat-sep" />
        <div className="sv2-stat">
          <span className="sv2-stat-value">{summary.propertyKeyCount}</span>
          <span className="sv2-stat-label">Property Keys</span>
        </div>
      </div>

      {/* ── Two-column tables ───────────────────────────────────────────── */}
      <div className="sv2-tables">
        {/* NODES */}
        <div className="sv2-panel">
          <div className="sv2-panel-header">
            <span className="sv2-panel-title">Nodes <span className="sv2-panel-count">({nodeRows.length})</span></span>
            <input
              className="sv2-search"
              placeholder="Filter labels…"
              value={nodeFilter}
              onChange={e => setNodeFilter(e.target.value)}
            />
          </div>
          <div className="sv2-table-wrap">
            <table className="sv2-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Label</th>
                  <th className="sv2-th-bar">Distribution</th>
                  <th className="sv2-th-num sv2-sortable" onClick={toggleNodeSort}>
                    Count {nodeSort === 'desc' ? '↓' : '↑'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {nodeRows.map(([label, count]) => {
                  const nodeProps = schema?.nodes?.[label]?.properties ?? {};
                  const propCount = Object.keys(nodeProps).length;
                  const expanded = expandedNodes.has(label);
                  return (
                    <React.Fragment key={label}>
                      <tr
                        className={`sv2-expandable-row${propCount > 0 ? ' sv2-has-props' : ''}`}
                        onClick={() => propCount > 0 && toggleNode(label)}
                      >
                        <td className="sv2-td-chevron">
                          {propCount > 0 && <span className="sv2-chevron">{expanded ? '▾' : '▸'}</span>}
                        </td>
                        <td className="sv2-td-label">{label}</td>
                        <td className="sv2-td-bar">
                          <Bar
                            value={count}
                            max={maxNodeCount}
                            pctLabel={summary.nodeCount > 0 ? `${((count / summary.nodeCount) * 100).toFixed(1)}%` : '0%'}
                          />
                        </td>
                        <td className="sv2-td-num">{fmt(count)}</td>
                      </tr>
                      {expanded && propCount > 0 && (
                        <tr className="sv2-detail-row">
                          <td />
                          <td colSpan={3} className="sv2-detail-cell">
                            <PropRows props={nodeProps as Record<string, { type: string }>} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* RELATIONSHIPS */}
        <div className="sv2-panel">
          <div className="sv2-panel-header">
            <span className="sv2-panel-title">Relationships <span className="sv2-panel-count">({relRows.length})</span></span>
            <input
              className="sv2-search"
              placeholder="Filter types…"
              value={relFilter}
              onChange={e => setRelFilter(e.target.value)}
            />
          </div>
          <div className="sv2-table-wrap">
            <table className="sv2-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Type</th>
                  <th>Source → Target</th>
                  <th className="sv2-th-bar">Distribution</th>
                  <th className="sv2-th-num sv2-sortable" onClick={toggleRelSort}>
                    Count {relSort === 'desc' ? '↓' : '↑'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {relRows.map((r, i) => {
                  const key = `${r.type}-${r.source}-${r.target}-${i}`;
                  const relProps = schema?.edges?.[r.type]?.properties ?? {};
                  const propCount = Object.keys(relProps).length;
                  const expanded = expandedRels.has(key);
                  return (
                    <React.Fragment key={key}>
                      <tr
                        className={`sv2-expandable-row${propCount > 0 ? ' sv2-has-props' : ''}`}
                        onClick={() => propCount > 0 && toggleRel(key)}
                      >
                        <td className="sv2-td-chevron">
                          {propCount > 0 && <span className="sv2-chevron">{expanded ? '▾' : '▸'}</span>}
                        </td>
                        <td className="sv2-td-label">{r.type}</td>
                        <td className="sv2-td-conn">
                          <span className="sv2-node-chip">{r.source}</span>
                          <span className="sv2-arrow">→</span>
                          <span className="sv2-node-chip">{r.target}</span>
                        </td>
                        <td className="sv2-td-bar">
                          <Bar
                            value={r.count}
                            max={maxRelCount}
                            pctLabel={totalRelFlat > 0 ? `${((r.count / totalRelFlat) * 100).toFixed(1)}%` : '0%'}
                          />
                        </td>
                        <td className="sv2-td-num">{fmt(r.count)}</td>
                      </tr>
                      {expanded && propCount > 0 && (
                        <tr className="sv2-detail-row">
                          <td />
                          <td colSpan={4} className="sv2-detail-cell">
                            <PropRows props={relProps as Record<string, { type: string }>} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryViewer;
